import {
  faCheckCircle,
  faTag,
  faImage,
  faClock,
  faIndustry,
  faBoxes,
  faChartLine,
  faSeedling,
  faSpinner,
  faBan,
  faTimesCircle,
  faDatabase,
  faCloudUploadAlt,
  faPaperPlane,
} from '@fortawesome/free-solid-svg-icons';

import DashboardWidgetType from 'lib/enums/DashboardWidgetTypes';
import {
  softGreen,
  softGreenDark,
  softGreenLight,
  softBlue,
  softBlueDark,
  softBlueLight,
  softAmber,
  softAmberDark,
  softAmberLight,
  softPurple,
  softPurpleDark,
  softPurpleLight,
  softTealPrimary,
  softTeal,
  softTealLight,
  softIndigo,
  softIndigoDark,
  lightIndigo,
  modernSalmon,
  darkerSalmon,
  modernSalmonBackground,
} from 'lib/css/colors';

/**
 * Dashboard Widget Configuration
 *
 * Each widget type has a visual configuration with:
 * - icon: FontAwesome icon to display
 * - gradient: [primaryColor, secondaryColor] for the accent bar
 * - iconBg: Background color for the icon container
 * - iconColor: Color of the icon
 *
 * Themes:
 * - Green: Verification/completed widgets
 * - Blue: Image/media related widgets
 * - Amber: Pending/warning widgets
 * - Purple: SKU/product related widgets
 * - Teal: DP (Distributor Partner) products
 * - Indigo: Default/other widgets
 */
const WIDGET_CONFIGS = {
  // Verification widgets - Green theme
  [DashboardWidgetType.VERIFIED_VENDOR_PRODUCTS]: {
    icon: faCheckCircle,
    gradient: [softGreen, softGreenDark],
    iconBg: softGreenLight,
    iconColor: softGreenDark,
  },
  [DashboardWidgetType.VERIFIED_BRANDS]: {
    icon: faTag,
    gradient: [softGreen, softGreenDark],
    iconBg: softGreenLight,
    iconColor: softGreenDark,
  },
  [DashboardWidgetType.VERIFIED_MANUFACTURERS]: {
    icon: faIndustry,
    gradient: [softGreen, softGreenDark],
    iconBg: softGreenLight,
    iconColor: softGreenDark,
  },
  [DashboardWidgetType.VERIFIED_SIMILAR_BRANDS]: {
    icon: faCheckCircle,
    gradient: [softGreen, softGreenDark],
    iconBg: softGreenLight,
    iconColor: softGreenDark,
  },
  [DashboardWidgetType.CONTENT_VERIFIED_MANUFACTURER_PRODUCTS]: {
    icon: faBoxes,
    gradient: [softGreen, softGreenDark],
    iconBg: softGreenLight,
    iconColor: softGreenDark,
  },
  [DashboardWidgetType.RESOLVED_BRANDS]: {
    icon: faCheckCircle,
    gradient: [softGreen, softGreenDark],
    iconBg: softGreenLight,
    iconColor: softGreenDark,
  },

  // Image/Media widgets - Blue theme
  [DashboardWidgetType.TAGGED_IMAGES]: {
    icon: faImage,
    gradient: [softBlue, softBlueDark],
    iconBg: softBlueLight,
    iconColor: softBlueDark,
  },
  [DashboardWidgetType.CONTENT_VERIFIED_BRANDS]: {
    icon: faCheckCircle,
    gradient: [softBlue, softBlueDark],
    iconBg: softBlueLight,
    iconColor: softBlueDark,
  },

  // Pending widgets - Amber/Orange theme
  [DashboardWidgetType.VERIFICATION_PENDING_BRANDS]: {
    icon: faClock,
    gradient: [softAmber, softAmberDark],
    iconBg: softAmberLight,
    iconColor: softAmberDark,
  },
  [DashboardWidgetType.VERIFICATION_PENDING_MANUFACTURERS]: {
    icon: faClock,
    gradient: [softAmber, softAmberDark],
    iconBg: softAmberLight,
    iconColor: softAmberDark,
  },
  [DashboardWidgetType.UNVERIFIED_BRANDS_IN_DISTRIBUTOR_PARTNERS]: {
    icon: faClock,
    gradient: [softAmber, softAmberDark],
    iconBg: softAmberLight,
    iconColor: softAmberDark,
  },
  [DashboardWidgetType.UNCLASSIFIED_ACTIVE_DISTRIBUTOR_PARTNER_PRODUCTS]: {
    icon: faBoxes,
    gradient: [softAmber, softAmberDark],
    iconBg: softAmberLight,
    iconColor: softAmberDark,
  },
  [DashboardWidgetType.UNCLASSIFIED_VERIFIED_MFR_PRODUCTS]: {
    icon: faBoxes,
    gradient: [softAmber, softAmberDark],
    iconBg: softAmberLight,
    iconColor: softAmberDark,
  },

  [DashboardWidgetType.UNVERIFIED_BRANDS]: {
    icon: faTimesCircle,
    gradient: [modernSalmon, darkerSalmon],
    iconBg: modernSalmonBackground,
    iconColor: modernSalmon,
  },
  [DashboardWidgetType.UNVERIFIABLE_BRANDS]: {
    icon: faBan,
    gradient: [softPurple, softPurpleDark],
    iconBg: softPurpleLight,
    iconColor: softPurpleDark,
  },

  [DashboardWidgetType.OUTLIER_CLEANUP_PENDING]: {
    icon: faClock,
    gradient: [softAmber, softAmberDark],
    iconBg: softAmberLight,
    iconColor: softAmberDark,
  },
  [DashboardWidgetType.OUTLIER_CLEANUP_SENT_FOR_DETECTION]: {
    icon: faPaperPlane,
    gradient: [softBlue, softBlueDark],
    iconBg: softBlueLight,
    iconColor: softBlueDark,
  },
  [DashboardWidgetType.OUTLIER_CLEANUP_CLEANED]: {
    icon: faCheckCircle,
    gradient: [softGreen, softGreenDark],
    iconBg: softGreenLight,
    iconColor: softGreenDark,
  },

  // Product/SKU widgets - Purple theme
  [DashboardWidgetType.KNOWN_SKU_MANUFACTURER_PRODUCTS]: {
    icon: faBoxes,
    gradient: [softPurple, softPurpleDark],
    iconBg: softPurpleLight,
    iconColor: softPurpleDark,
  },
  [DashboardWidgetType.KNOWN_SKUS_ENRICHED_BY_MANUFACTURERS]: {
    icon: faBoxes,
    gradient: [softPurple, softPurpleDark],
    iconBg: softPurpleLight,
    iconColor: softPurpleDark,
  },
  [DashboardWidgetType.KNOWN_SKU_MANUFACTURER_PRODUCTS_WITH_SCORE_ABOVE_60]: {
    icon: faChartLine,
    gradient: [softPurple, softPurpleDark],
    iconBg: softPurpleLight,
    iconColor: softPurpleDark,
  },
  [DashboardWidgetType.ENGAGED_MANUFACTURERS]: {
    icon: faIndustry,
    gradient: [softPurple, softPurpleDark],
    iconBg: softPurpleLight,
    iconColor: softPurpleDark,
  },

  // DP Products - Teal theme
  [DashboardWidgetType.ACTIVE_DP_PRODUCTS]: {
    icon: faBoxes,
    gradient: [softTealPrimary, softTeal],
    iconBg: softTealLight,
    iconColor: softTeal,
  },
  [DashboardWidgetType.ACTIVE_DP_PRODUCTS_SKU_MAPPED]: {
    icon: faBoxes,
    gradient: [softTealPrimary, softTeal],
    iconBg: softTealLight,
    iconColor: softTeal,
  },
  [DashboardWidgetType.SKU_MAPPED_DP_PRODUCTS]: {
    icon: faBoxes,
    gradient: [softTealPrimary, softTeal],
    iconBg: softTealLight,
    iconColor: softTeal,
  },
  [DashboardWidgetType.DP_PRODUCTS_WITH_A_SALE_IN_1M]: {
    icon: faChartLine,
    gradient: [softTealPrimary, softTeal],
    iconBg: softTealLight,
    iconColor: softTeal,
  },
  [DashboardWidgetType.DP_PRODUCTS_WITH_A_SALE_IN_1M_AND_MAPPED_TO_A_VERIFIED_SKU]: {
    icon: faChartLine,
    gradient: [softTealPrimary, softTeal],
    iconBg: softTealLight,
    iconColor: softTeal,
  },
  [DashboardWidgetType.DP_PRODUCTS_WITH_A_SALE_IN_1M_AND_MAPPED_TO_A_KNOWN_SKU]: {
    icon: faChartLine,
    gradient: [softTealPrimary, softTeal],
    iconBg: softTealLight,
    iconColor: softTeal,
  },

  // Brand Harvesting widgets - Green theme
  PENDING: {
    icon: faClock,
    gradient: [softGreen, softGreenDark],
    iconBg: softGreenLight,
    iconColor: softGreenDark,
  },
  READY_FOR_SCRAPING: {
    icon: faSeedling,
    gradient: [softGreen, softGreenDark],
    iconBg: softGreenLight,
    iconColor: softGreenDark,
  },
  NOT_SCRAPABLE: {
    icon: faBan,
    gradient: [softAmber, softAmberDark],
    iconBg: softAmberLight,
    iconColor: softAmberDark,
  },
  ACQUIRING: {
    icon: faSpinner,
    gradient: [softGreen, softGreenDark],
    iconBg: softGreenLight,
    iconColor: softGreenDark,
  },
  SCRAPING_COMPLETED: {
    icon: faCheckCircle,
    gradient: [softGreen, softGreenDark],
    iconBg: softGreenLight,
    iconColor: softGreenDark,
  },
  SCRAPPED_FILE_REJECTED: {
    icon: faTimesCircle,
    gradient: [softAmber, softAmberDark],
    iconBg: softAmberLight,
    iconColor: softAmberDark,
  },
  DATA_MAPPING: {
    icon: faDatabase,
    gradient: [softGreen, softGreenDark],
    iconBg: softGreenLight,
    iconColor: softGreenDark,
  },
  UPLOADED_TO_UPB: {
    icon: faCloudUploadAlt,
    gradient: [softGreen, softGreenDark],
    iconBg: softGreenLight,
    iconColor: softGreenDark,
  },
};

// Default config for widgets not explicitly defined
const DEFAULT_WIDGET_CONFIG = {
  icon: faChartLine,
  gradient: [softIndigo, softIndigoDark],
  iconBg: lightIndigo,
  iconColor: softIndigoDark,
};

/**
 * Get the configuration for a specific widget type
 * Falls back to DEFAULT_WIDGET_CONFIG if not found
 */
export const getWidgetConfig = widgetType => {
  return WIDGET_CONFIGS[widgetType] || DEFAULT_WIDGET_CONFIG;
};

export { WIDGET_CONFIGS, DEFAULT_WIDGET_CONFIG };
